import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { SettingDrawer } from '../mobile/setting-drawer';
import featuresInfo from '../../helpers/state/features-info';
import topicData from '../../helpers/state/topic-data';

@customElement('map-drawer-component')
export class MapDesktopDrawer extends LitElement {
    @query('#settingDrawer')
    settingDrawer!: SettingDrawer;

    @property({ type: Boolean })
    openInitially: boolean = false;

    @property({ type: Boolean })
    commandCenter: boolean = false;

    @property({ type: Boolean })
    isMobile: boolean = false;

    @property({ type: Boolean })
    showClose: boolean = true;

    @state()
    mobileExpanded: boolean = true;

    @property()
    backgroundColor: string = 'white';

    @state()
    title: string = '';

    @property()
    coverImage: string = '';

    @property()
    coverImageMaxHeight: string = '180px';

    @property()
    coverImageLogo: string = '';

    @property({ type: Boolean })
    coverImageLogoBackground: boolean = true;

    @property({ type: Boolean })
    fullHeightOnExpand: boolean = false;

    @state()
    topLeftIcon?: string;

    // listen for changes to top left icon
    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topLeftIcon')) {
            this.requestUpdate();
        }
    }

    async close() {
        this.dispatchEvent(new CustomEvent('on-close'));
    }

    protected render() {
        return topicData.mapComponent?.isMobile === true ? this.renderMobile() : this.renderDesktop();
    }

    get hideBuildYourOwnButton() {
        return topicData.isWriter
            || (topicData.topic?.disable_command_center_buttons === true && featuresInfo.hideAllCommandCenterButtonsEnabled)
            || this.isMobile
            || topicData.topic?.disable_build_your_own === true;
    }

    private async buildMap() {
        const link = document.createElement('a');
        link.href = `https://www.proxi.co/?utm_source=embed&utm_medium=referral&utm_content=build_your_own&utm_term=${topicData.topicId}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
    }

    renderDesktop() {
        return html`
            <div class="drawer__holder" style="background-color:${this.backgroundColor}">
                ${this.coverImage ? html`
                    <div class="drawer__cover-image-container" style="height:${this.coverImageMaxHeight}; max-height:${this.coverImageMaxHeight}">
                        <img src="${this.coverImage}" class="drawer__cover-image" />
                        ${this.coverImageLogo ? html`
                            <div class="drawer__cover-image-logo-overlay">
                                <img src="${this.coverImageLogo}" alt="Logo" style="${this.coverImageLogoBackground ? 'background-color: white;' : 'background-color: transparent; box-shadow: none;'}" />
                            </div>
                        ` : ''}
                        <div class="drawer__cover-image-controls">
                            ${this.topLeftIcon ? html`
                                <img src="${this.topLeftIcon}" style="width:24px; height:24px; border-radius:50%" alt="icon preview" />
                            ` : html`<div></div>`}
                            <mwc-icon class="drawer__cover-image-icon" @click="${async () => await this.close()}">close</mwc-icon>
                        </div>
                    </div>
                    <div class="drawer__controls" style="border-bottom:none">
                        <div class="spacer" style="width:24px"></div>
                        <h3 style="margin:5px 0; text-align:center">${this.title}</h3>
                        <div class="spacer" style="width:24px"></div>
                    </div>
                ` : html`
                    <div class="drawer__controls">
                        ${this.topLeftIcon ? html `
                            <div class="spacer" style="width:24px"><img src="${this.topLeftIcon}" style="width:24px; height:24px" alt="icon preview" /></div>
                        ` : html`
                            <div class="spacer" style="width:24px"></div>`
                        }
                        <h3 style="margin:5px 0; text-align:center">${this.title}</h3>
                        <mwc-icon style="cursor:pointer" @click="${async () => await this.close()}">close</mwc-icon>
                    </div>
                `}
                <div class="setting-drawer__content">
                    <slot></slot>
                </div>

            </div>
            ${this.hideBuildYourOwnButton ? html`` : html`
                <div class="drawer-buttons-group build-your-own-group" style="background-color:${topicData.backgroundColor}; color:${topicData.textColor}" @click="${this.buildMap}">
                    <mwc-icon style="margin-right:5px">
                        map
                    </mwc-icon>
                    <translated-span>
                        Build Your Own Proxi Map
                    </translated-span>
                </div>
            `}
        `;
    }

    renderMobile() {
        const isAdminPortalMobile = topicData.mapComponent?.isAdminPortalMobile === true;
        return html`
            <setting-drawer id="settingDrawer" .isExpanded="${this.mobileExpanded}" .openInitially="${true}" style="--drawer-bg-color:${this.backgroundColor};--drawer-border-color:${this.backgroundColor}" .adminPortalMobile=${isAdminPortalMobile}
                title="${this.title}" coverImage="${this.coverImage}" coverImageMaxHeight="${this.coverImageMaxHeight}" coverImageLogo="${this.coverImageLogo}" .coverImageLogoBackground=${this.coverImageLogoBackground} .fullHeightOnExpand=${this.fullHeightOnExpand} @on-close="${async () => await this.close()}">
                <slot></slot>
            </setting-drawer>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                width: 100%;
            }

            .build-your-own-group {
                background-color: #102C3D;
                color: #ffffff;
                padding: 10px;
                --mdc-icon-size: 16px;
                display: flex;
                align-items: center;
                cursor: pointer;
                justify-content: center;
                font-weight: 500;
                letter-spacing: 0.5px;
            }

            setting-drawer {
                --bottom-height: 0px;
                transition: height 0.1s ease-in-out;
            }

            .drawer__controls {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .drawer__holder {
                padding: 10px 20px;
                display: flex;
                flex-direction: column;
                flex: 1;
                border-radius: 4px 0 0 4px;
                overflow: auto;
            }

            .drawer__cover-image-container {
                position: relative;
                margin: -10px -20px 0;
                overflow: hidden;
            }

            .drawer__cover-image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            .drawer__cover-image-logo-overlay {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                justify-content: center;
                align-items: center;
                width: 70%;
                height: 70%;
                z-index: 2;
            }

            .drawer__cover-image-logo-overlay img {
                max-width: min(100%, 260px);
                max-height: min(100%, 96px);
                object-fit: contain;
                background-color: white;
                border-radius: 6px;
                padding: 5px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            }

            .drawer__cover-image-controls {
                position: absolute;
                top: 8px;
                left: 12px;
                right: 12px;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .drawer__cover-image-icon {
                background: rgba(0, 0, 0, 0.4);
                color: white;
                border-radius: 50%;
                padding: 4px;
                cursor: pointer;
            }

            .setting-drawer__content {
                display: flex;
                flex: 1;
                overflow: hidden;
            }
        `;
    }
}
